import { PathRef, Node } from 'domains/reporter/RichTextEditor/core';
import { atom } from 'recoil';
import type { RecoilState } from 'recoil';
import {
  localStoragePersisterEffect,
  broadcastChannelSynchronizerEffect,
  CONDUCTORS,
} from 'utils/recoilEffects';
import type { Key } from 'slate-react';
import type { PlaceholderPluginElement } from '../RichTextEditor/plugins/placeholder/types';
import type { ProcedureRelevance } from 'common/Procedures';
import { NOOP } from 'config/constants';
import type { ReportTemplateFieldsFragment } from 'generated/graphql';

export const ReportStatus: {
  Init: 'init';
  Idle: 'idle';
  Submitting: 'submitting';
  ProvisionalSubmit: 'provisionalReportSubmit';
  Submitted: 'submitted';
  Cancelling: 'cancelling';
  SubmittingPrelim: 'submittingPrelim';
  ProvisionalPrelimSubmit: 'provisionalPrelimReportSubmit';
  CancellingPrelim: 'cancellingPrelim';
  AddendumAdd: 'addendumAdd';
  AddendumSubmitting: 'addendumSubmitting';
  ProvisionalAddendumSubmit: 'provisionalAddendumSubmit';
  AddendumCancelling: 'addendumCancelling';
} = Object.freeze({
  Init: 'init',
  Idle: 'idle',
  Submitting: 'submitting',
  ProvisionalSubmit: 'provisionalReportSubmit',
  Submitted: 'submitted',
  Cancelling: 'cancelling',
  SubmittingPrelim: 'submittingPrelim',
  ProvisionalPrelimSubmit: 'provisionalPrelimReportSubmit',
  CancellingPrelim: 'cancellingPrelim',
  AddendumAdd: 'addendumAdd',
  AddendumSubmitting: 'addendumSubmitting',
  ProvisionalAddendumSubmit: 'provisionalAddendumSubmit',
  AddendumCancelling: 'addendumCancelling',
});

export type TReportStatus = typeof ReportStatus;
export type ReportStatuses = TReportStatus[keyof TReportStatus];
export type TTouchedFieldState = Map<Key, PathRef>;

export const REPORT_EDITABLE_STATUSES: [
  TReportStatus['Init'],
  TReportStatus['Idle'],
  TReportStatus['AddendumAdd'],
] = [ReportStatus.Init, ReportStatus.Idle, ReportStatus.AddendumAdd];

export const PROVISIONAL_SUBMIT_STATUSES: [
  TReportStatus['ProvisionalSubmit'],
  TReportStatus['ProvisionalAddendumSubmit'],
  TReportStatus['ProvisionalPrelimSubmit'],
] = [
  ReportStatus.ProvisionalSubmit,
  ReportStatus.ProvisionalAddendumSubmit,
  ReportStatus.ProvisionalPrelimSubmit,
];

export const IN_FLIGHT_STATUSES: [
  TReportStatus['Submitting'],
  TReportStatus['Cancelling'],
  TReportStatus['AddendumSubmitting'],
  TReportStatus['AddendumCancelling'],
] = [
  ReportStatus.Submitting,
  ReportStatus.Cancelling,
  ReportStatus.AddendumSubmitting,
  ReportStatus.AddendumCancelling,
];

export const IN_FLIGHT_PRELIM_STATUSES: [
  TReportStatus['SubmittingPrelim'],
  TReportStatus['CancellingPrelim'],
] = [ReportStatus.SubmittingPrelim, ReportStatus.CancellingPrelim];

export const ADDENDUM_STATUSES: [
  TReportStatus['AddendumAdd'],
  TReportStatus['ProvisionalAddendumSubmit'],
  TReportStatus['AddendumSubmitting'],
  TReportStatus['AddendumCancelling'],
] = [
  ReportStatus.AddendumAdd,
  ReportStatus.ProvisionalAddendumSubmit,
  ReportStatus.AddendumSubmitting,
  ReportStatus.AddendumCancelling,
];

export const SECTION_EDITOR_EDITABLE_STATUSES: [
  TReportStatus['Idle'],
  TReportStatus['Submitting'],
] = [ReportStatus.Idle, ReportStatus.Submitting];

export const ADDENDUM_EDITOR_EDITABLE_STATUSES: [TReportStatus['AddendumAdd']] = [
  ReportStatus.AddendumAdd,
];

export const DISPLAY_DISCARD_REPORT_BUTTON_STATUSES: [
  TReportStatus['Idle'],
  TReportStatus['Submitting'],
  TReportStatus['ProvisionalSubmit'],
  TReportStatus['Cancelling'],
] = [
  ReportStatus.Idle,
  ReportStatus.Submitting,
  ReportStatus.ProvisionalSubmit,
  ReportStatus.Cancelling,
];

export const DISPLAY_PRELIM_BUTTON_STATUSES: [
  TReportStatus['Init'],
  TReportStatus['Idle'],
  TReportStatus['Cancelling'],
  TReportStatus['SubmittingPrelim'],
  TReportStatus['ProvisionalPrelimSubmit'],
  TReportStatus['CancellingPrelim'],
] = [
  ReportStatus.Init,
  ReportStatus.Idle,
  ReportStatus.Cancelling,
  ReportStatus.SubmittingPrelim,
  ReportStatus.ProvisionalPrelimSubmit,
  ReportStatus.CancellingPrelim,
];

export const reportStatusState: RecoilState<ReportStatuses> = atom<ReportStatuses>({
  key: 'reportStatusState',
  default: ReportStatus.Init,
  effects: [broadcastChannelSynchronizerEffect({ conductor: CONDUCTORS.reporter, debugLog: true })],
});

type ReporterPreferences = {
  sidebarSize: number;
  sidebarLastExpandedSize: number;
  isSidebarOpen: boolean;
};

export const reporterRightDrawerState: RecoilState<ReporterPreferences> = atom<ReporterPreferences>(
  {
    key: 'reporterRightDrawerState',
    default: {
      sidebarSize: 340,
      sidebarLastExpandedSize: 340,
      isSidebarOpen: true,
    },
    effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
  }
);

export enum InsertedTemplateDataSource {
  RightDrawerDoubleClick = 'double-click',
  RightDrawerSingleClick = 'click',
  VoiceCommand = 'voice-command',
  Autoload = 'autoload',
}

export type InsertedTemplateData = {
  templateId: string;
  source: InsertedTemplateDataSource;
  hasConfirmationDialogTriggered: boolean;
};
/**
 * This is used to keep track of the inserted template data.
 * For metrics usage.
 */
export const insertedTemplateMetricsState: RecoilState<InsertedTemplateData | null> =
  atom<InsertedTemplateData | null>({
    key: 'insertedTemplate',
    default: null,
  });

export type TemplateInsertionCompletionState = {
  sequence: number;
  templateId: string | null;
  source: InsertedTemplateDataSource | null;
};

export const templateInsertionCompletionState: RecoilState<TemplateInsertionCompletionState> =
  atom<TemplateInsertionCompletionState>({
    key: 'templateInsertionCompletion',
    default: {
      sequence: 0,
      templateId: null,
      source: null,
    },
  });

/**
 * Increments when reporter actions complete that should refresh right drawer catalogs
 * (templates/macros), for example successful submit/autoload flows.
 */
export const rightDrawerCatalogRefreshSequenceState: RecoilState<number> = atom<number>({
  key: 'rightDrawerCatalogRefreshSequence',
  default: 0,
});

export type TemplateInsertRequestState = {
  requestId: string;
  templateId: string;
  source: InsertedTemplateDataSource;
  worklistItemSmid: string;
};

/**
 * Explicit command state used to trigger template insertion exactly once per request.
 * This avoids inferring insertion intent from ambient state changes.
 */
export const templateInsertRequestState: RecoilState<TemplateInsertRequestState | null> =
  atom<TemplateInsertRequestState | null>({
    key: 'templateInsertRequest',
    default: null,
  });

export type PendingTemplateState = {
  templateId: string;
  source: InsertedTemplateDataSource;
};

export const pendingTemplateIdState: RecoilState<Nullable<PendingTemplateState>> = atom<
  Nullable<PendingTemplateState>
>({
  key: 'pendingTemplateId',
  default: null,
});

// Blocks autoload template resolution while claim-driven refetch queries are in flight.
export const claimingWorklistSmidState: RecoilState<Nullable<string>> = atom<Nullable<string>>({
  key: 'claimingWorklistSmid',
  default: null,
});

// Tracks whether any Link Cases mutation is currently queued or in-flight.
export const linkCasesMutationCountState: RecoilState<number> = atom<number>({
  key: 'linkCasesMutationCountState',
  default: 0,
});

export const touchedRequiredFieldsState: RecoilState<TTouchedFieldState> = atom<TTouchedFieldState>(
  {
    key: 'touchedRequiredFieldsState',
    default: new Map<Key, PathRef>(),
  }
);

export type TEditableState = 'readOnly' | 'menuOpen' | 'editing';
export const editableStudyDeepLinkState: RecoilState<Map<Key, TEditableState>> = atom<
  Map<Key, TEditableState>
>({
  key: 'editableStudyDeepLinkState',
  default: new Map<Key, TEditableState>(),
});

export const dictationQueueSizeState: RecoilState<{
  pendingDictations: number;
  pendingWebsocketDictations: number;
  openWebsocketConnections: number;
}> = atom<{
  pendingDictations: number;
  pendingWebsocketDictations: number;
  openWebsocketConnections: number;
}>({
  key: 'dictationQueueSizeState',
  default: {
    pendingDictations: 0,
    pendingWebsocketDictations: 0,
    openWebsocketConnections: 0,
  },
});

export const fieldBuilderMode: RecoilState<'text' | 'picklist'> = atom<'text' | 'picklist'>({
  key: 'fieldBuilderMode',
  default: 'text',
});

export const headingErrorState: RecoilState<boolean> = atom<boolean>({
  key: 'headingErrorState',
  default: false,
});

export const autoCorrectModalState: RecoilState<{
  open: boolean;
  selectionText: string;
}> = atom<{
  open: boolean;
  selectionText: string;
}>({
  key: 'autoCorrectModal',
  default: {
    open: false,
    selectionText: '',
  },
});

export const hasReportContentChangedState: RecoilState<boolean> = atom<boolean>({
  key: 'hasReportContentChanged',
  default: false,
  effects: [broadcastChannelSynchronizerEffect({ debugLog: true })],
});

export const emptyPlaceholderDialogState: RecoilState<{
  emptyPlaceholders: PlaceholderPluginElement[];
  open: boolean;
}> = atom<{
  emptyPlaceholders: PlaceholderPluginElement[];
  open: boolean;
}>({
  key: 'placeholderFieldDialog',
  default: {
    emptyPlaceholders: [],
    open: false,
  },
});

export const DEFAULT_REQUIRED_FIELDS_DIALOG_STATE = {
  deleteCallback: NOOP,
  open: false,
  isMultiple: false,
} as const;

export const confirmDeletionRequiredFieldsDialogState: RecoilState<{
  deleteCallback: () => void;
  open: boolean;
  isMultiple: boolean;
}> = atom<{
  deleteCallback: () => void;
  open: boolean;
  isMultiple: boolean;
}>({
  key: 'confirmDeletionRequiredFieldsDialog',
  default: DEFAULT_REQUIRED_FIELDS_DIALOG_STATE,
});

export type EmptyRequiredFieldsDialogState =
  | {
      emptyRequiredFields: [];
      open: false;
      worklistItemSmid: null;
    }
  | {
      emptyRequiredFields: Node[];
      open: true;
      worklistItemSmid: string;
    };

export const emptyRequiredFieldsDialogStateDefault: EmptyRequiredFieldsDialogState = {
  emptyRequiredFields: [],
  open: false,
  worklistItemSmid: null,
};

export const emptyRequiredFieldsDialogRecoilState: RecoilState<EmptyRequiredFieldsDialogState> =
  atom<EmptyRequiredFieldsDialogState>({
    key: 'emptyRequiredFieldsDialog',
    default: emptyRequiredFieldsDialogStateDefault,
  });

export const proceduresRelevanceState: RecoilState<ProcedureRelevance> = atom<ProcedureRelevance>({
  key: 'proceduresRelevance',
  default: 'global',
});

type DiscardReportState = {
  open: boolean;
  discarding: boolean;
  worklistItemSmid: Nullable<string>;
  error: string | null;
};

export const discardReportStateDefault = {
  open: false,
  discarding: false,
  worklistItemSmid: null,
  error: null,
} as const;

/*
 * There is a workflow where the user can discard a draft from the worklist.
 * Therefore, this atom needs to manage its state across tabs.
 * This mergeFn is used to reset the state when the user refreshes the reporter page.
 * Practically, this means they can "recover" from the discard action,
 * and view a claimable empty report as opposed to a browse worklist component.
 */
const discardReportMergeFn = (
  oldValue: Nullable<DiscardReportState>,
  newValue: DiscardReportState
): DiscardReportState => {
  if (
    oldValue == null &&
    newValue.worklistItemSmid != null &&
    newValue.open === discardReportStateDefault.open &&
    newValue.discarding === discardReportStateDefault.discarding &&
    newValue.error === discardReportStateDefault.error
  ) {
    return {
      ...discardReportStateDefault,
    };
  }
  return newValue;
};

export const discardReportState: RecoilState<DiscardReportState> = atom<DiscardReportState>({
  key: 'discardReportDialog',
  default: discardReportStateDefault,
  effects: [broadcastChannelSynchronizerEffect({ mergeFn: discardReportMergeFn })],
});

export const prevGeneratedImpressionState: RecoilState<string | null> = atom<string | null>({
  key: 'prevGeneratedImpression',
  default: null,
});

export const draftReportWorklistSmid: RecoilState<Nullable<string>> = atom<Nullable<string>>({
  key: 'draftReport',
  default: null,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const prelimReportWorklistSmid: RecoilState<string | null> = atom<string | null>({
  key: 'prelimReportWorklistSmid',
  default: null,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const prevReportSectionsState: RecoilState<string | null> = atom<string | null>({
  key: 'prevReportSections',
  default: null,
});

export const isFocusModeState: RecoilState<boolean> = atom<boolean>({
  key: 'isFocusMode',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const isFocusModeSettingEnabledState: RecoilState<boolean> = atom<boolean>({
  key: 'isFocusModeSettingEnabled',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export type TUnsignedReportWarningState = {
  open: boolean;
  worklistActionQueued: boolean;
  readyForWorklistAction: boolean;
  id: string;
  eventType: '' | 'read' | 'view';
};

export const defaultUnsignedReportWarningDialogState: TUnsignedReportWarningState = Object.freeze({
  open: false,
  worklistActionQueued: false,
  readyForWorklistAction: false,
  id: '',
  eventType: '',
});

export const unsignedReportDialogState: RecoilState<TUnsignedReportWarningState> =
  atom<TUnsignedReportWarningState>({
    key: 'unsignedReportDialogState',
    default: defaultUnsignedReportWarningDialogState,
    effects: [broadcastChannelSynchronizerEffect({ debugLog: true })],
  });

export const isReporterLockedState: RecoilState<boolean> = atom<boolean>({
  key: 'isReporterLockedState',
  default: false,
  effects: [broadcastChannelSynchronizerEffect()],
});

export const reporterIsBypassAndSign: RecoilState<boolean> = atom<boolean>({
  key: 'reporterIsBypassAndSign',
  default: false,
});

export const reporterIsBypassAndSignPrelim: RecoilState<boolean> = atom<boolean>({
  key: 'reporterIsBypassAndSignPrelim',
  default: false,
});

export const reportVersionState: RecoilState<Nullable<number>> = atom<Nullable<number>>({
  key: 'reportVersionState',
  default: null,
});

export const reportTemplateState: RecoilState<Nullable<ReportTemplateFieldsFragment>> = atom<
  Nullable<ReportTemplateFieldsFragment>
>({
  key: 'reportTemplateState',
  default: null,
});

export const lineIndicatorState: RecoilState<Range | null> = atom<Range | null>({
  key: 'lineIndicatorState',
  default: null,
});
